import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Loader2, RefreshCw, ClipboardList, CalendarDays, Trophy, ChevronDown, ChevronUp, CheckCircle2, AlertCircle, FileText } from "lucide-react";

const TYPE_ICON = { tryout: Trophy, recognize: ClipboardList, posttest: CalendarDays, asesmen: FileText };
const TYPE_BADGE = {
  tryout: "bg-[#E25E3E]/12 text-[#E25E3E]",
  recognize: "bg-[#4A6B53]/12 text-[#4A6B53]",
  posttest: "bg-[#F4B942]/20 text-[#8a6d16]",
  asesmen: "bg-[#3E6B8A]/12 text-[#3E6B8A]",
};

export default function AdminKelolaUpload() {
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmKey, setConfirmKey] = useState(null);
  const [busy, setBusy] = useState(false);
  const [openKey, setOpenKey] = useState(null);
  const [details, setDetails] = useState({});
  const [detailLoading, setDetailLoading] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/uploads"); setUploads(data.uploads || []); }
    catch { toast.error("Gagal memuat daftar upload"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const toggleDetail = async (u, key) => {
    if (openKey === key) { setOpenKey(null); return; }
    setOpenKey(key);
    if (details[key]) return;
    setDetailLoading(key);
    try {
      const { data } = await api.get(`/admin/uploads/${u.type}/${u.id}/detail`);
      setDetails((p) => ({ ...p, [key]: data }));
    } catch { toast.error("Gagal memuat detail siswa"); setOpenKey(null); }
    setDetailLoading(null);
  };

  const del = async (u) => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/admin/uploads/${u.type}/${u.id}`);
      toast.success(`Terhapus: ${data.deleted_records} data (${data.affected_users} siswa terpengaruh)`);
      setConfirmKey(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal menghapus"); }
    setBusy(false);
  };

  return (
    <AppShell>
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-1">
          <h1 className="font-heading font-black text-3xl text-[#1A1C1A]">Kelola / Hapus Upload</h1>
          <button onClick={load} data-testid="refresh-uploads" className="text-sm text-[#5C5F5C] inline-flex items-center gap-1 hover:text-[#1A1C1A]">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Muat ulang
          </button>
        </div>
        <p className="text-[#5C5F5C] mb-6">Klik salah satu item untuk melihat daftar siswa yang cocok & yang belum ditemukan. Hapus PDF yang salah diunggah — menghapus akan menghapus data yang sudah didistribusikan ke Profilku & Dashboard Orang Tua siswa.</p>

        {loading ? (
          <div className="p-10 grid place-items-center text-[#9C9A94]"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : uploads.length === 0 ? (
          <p className="text-sm text-[#9C9A94]" data-testid="no-uploads">Belum ada file yang diunggah.</p>
        ) : (
          <div className="space-y-3">
            {uploads.map((u) => {
              const key = `${u.type}-${u.id}`;
              const Icon = TYPE_ICON[u.type] || ClipboardList;
              const isOpen = openKey === key;
              const det = details[key];
              return (
                <div key={key} className="clay-card p-4" data-testid={`upload-${u.id}`}>
                  <div className="flex items-center justify-between gap-3">
                    <button onClick={() => toggleDetail(u, key)} data-testid={`toggle-detail-${u.id}`} className="flex-1 min-w-0 text-left group">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-xs px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${TYPE_BADGE[u.type]}`}><Icon className="h-3 w-3" /> {u.type_label}</span>
                        <span className="font-semibold text-[#1A1C1A] truncate group-hover:underline">{u.batch_name || "(tanpa nama)"}</span>
                        {isOpen ? <ChevronUp className="h-4 w-4 text-[#9C9A94] shrink-0" /> : <ChevronDown className="h-4 w-4 text-[#9C9A94] shrink-0" />}
                      </div>
                      <p className="text-xs text-[#9C9A94] mt-1">
                        {u.tanggal ? `${u.tanggal} · ` : ""}
                        {u.matched != null ? `${u.matched} siswa cocok` : ""}{u.total != null ? ` / ${u.total} total` : ""}
                      </p>
                    </button>
                    {confirmKey === key ? (
                      <div className="flex items-center gap-2 shrink-0">
                        <button onClick={() => del(u)} disabled={busy} data-testid={`confirm-del-${u.id}`} className="px-3 py-1.5 rounded-full bg-[#D9381E] text-white text-sm font-medium disabled:opacity-60">{busy ? "Menghapus…" : "Ya, hapus"}</button>
                        <button onClick={() => setConfirmKey(null)} className="px-3 py-1.5 rounded-full border border-[#E5E0D8] text-sm text-[#5C5F5C]">Batal</button>
                      </div>
                    ) : (
                      <button onClick={() => setConfirmKey(key)} data-testid={`del-upload-${u.id}`} title="Hapus upload" className="p-2 rounded-lg text-[#D9381E] hover:bg-[#D9381E]/10 transition-colors shrink-0"><Trash2 className="h-4 w-4" /></button>
                    )}
                  </div>

                  {isOpen && (
                    <div className="mt-4 pt-4 border-t border-[#E5E0D8]" data-testid={`detail-${u.id}`}>
                      {detailLoading === key || !det ? (
                        <div className="py-6 grid place-items-center text-[#9C9A94]"><Loader2 className="h-5 w-5 animate-spin" /></div>
                      ) : (
                        <div className="grid sm:grid-cols-2 gap-4">
                          <div>
                            <p className="text-sm font-heading font-bold text-[#4A6B53] mb-2 flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4" /> Siswa yang cocok ({det.matched.length})</p>
                            {det.matched.length === 0 ? (
                              <p className="text-xs text-[#9C9A94]">Tidak ada siswa yang cocok.</p>
                            ) : (
                              <ul className="space-y-1.5" data-testid={`matched-list-${u.id}`}>
                                {det.matched.map((m, i) => (
                                  <li key={i} className="text-sm text-[#1A1C1A] flex items-start gap-2">
                                    <span className="text-[#4A6B53] mt-0.5">•</span>
                                    <span className="min-w-0"><span className="font-medium">{m.nama}</span>{m.kelas ? <span className="text-xs text-[#9C9A94]"> · Kelas {m.kelas}</span> : ""}{m.email ? <span className="block text-xs text-[#9C9A94] truncate">{m.email}</span> : ""}</span>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                          <div>
                            <p className="text-sm font-heading font-bold text-[#D9381E] mb-2 flex items-center gap-1.5"><AlertCircle className="h-4 w-4" /> Siswa tidak ditemukan ({det.unmatched.length})</p>
                            {det.unmatched.length === 0 ? (
                              <p className="text-xs text-[#9C9A94]">Semua nama berhasil dicocokkan.</p>
                            ) : (
                              <ul className="space-y-1.5" data-testid={`unmatched-list-${u.id}`}>
                                {det.unmatched.map((item, i) => {
                                  const nm = typeof item === "string" ? item : item.nama;
                                  const gagal = typeof item === "object" && (item.gagal || item.error);
                                  return (
                                    <li key={i} className="text-sm text-[#1A1C1A]" data-testid={`unmatched-item-${u.id}-${i}`}>
                                      <div className="flex items-start gap-2">
                                        <span className="text-[#D9381E] mt-0.5">•</span>
                                        <span className="min-w-0">
                                          {nm}
                                          {gagal && (
                                            <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-[#D9381E]/12 text-[#D9381E] align-middle">gagal ekstraksi</span>
                                          )}
                                          {gagal && item.error && (
                                            <span className="block text-[11px] text-[#D9381E] mt-0.5 leading-snug" data-testid={`unmatched-error-${u.id}-${i}`}>{item.error}</span>
                                          )}
                                        </span>
                                      </div>
                                    </li>
                                  );
                                })}
                              </ul>
                            )}
                            {det.unmatched.length > 0 && (
                              <p className="text-[11px] text-[#9C9A94] mt-2 leading-relaxed">Nama di atas belum cocok dengan akun manapun — periksa ejaan nama atau pastikan siswa sudah terdaftar.</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AppShell>
  );
}
